/** Piezas compartidas por los gráficos: escalas, ejes, leyenda y la capa de hover.
 *
 * Todo es SVG escrito a mano, sin librería. Tres razones: las especificaciones de marca del
 * skill `dataviz` (extremos de 4px anclados a la línea base, 2px de separación entre rellenos,
 * anillo de 2px en marcas que se solapan) se cumplen exactamente; el mismo SVG entra en el brief
 * exportado sin re-renderizar; y el bundle no arrastra dependencias.
 */
import { useId, useState } from "react";
import type { Bilingue, Idioma } from "../lib/tipos";
import { bi } from "../lib/i18n";
import { ANCHO_MOVIL, useEsMovil } from "../lib/movil";

export { ANCHO_MOVIL, useEsMovil };

/* Siete slots, no ocho: es el techo que el validador de la paleta permite con el magenta de
   marca en la primera posición (con un octavo, naranja y verde caen a ΔE 3.2 en protanopia).
   La octava serie va en gris, nunca en un tono generado. */
export const SERIES = ["var(--serie-1)", "var(--serie-2)", "var(--serie-3)", "var(--serie-4)",
                       "var(--serie-5)", "var(--serie-6)", "var(--serie-7)"] as const;
export const SECUENCIAL = ["var(--sec-0)", "var(--sec-1)", "var(--sec-2)", "var(--sec-3)", "var(--sec-4)", "var(--sec-5)"] as const;

/** El color de una serie por su POSICIÓN FIJA, nunca ciclado: un filtro que cambia el número de
 *  series no puede repintar a las que quedan. Pasada la octava, gris. */
export function colorSerie(i: number): string {
  return SERIES[i] ?? "var(--texto-3)";
}

export function pasoSecuencial(v: number, max: number): string {
  if (!Number.isFinite(v) || max <= 0 || v <= 0) return "var(--superficie-2)";
  const paso = Math.min(SECUENCIAL.length - 1, 1 + Math.floor((v / max) * (SECUENCIAL.length - 1.001)));
  return SECUENCIAL[paso]!;
}

export const MARGEN = { arriba: 16, derecha: 16, abajo: 34, izquierda: 52 };

export function escalaLineal(min: number, max: number, desde: number, hasta: number) {
  const rango = max - min || 1;
  return (v: number) => desde + ((v - min) / rango) * (hasta - desde);
}

export function ticks(min: number, max: number, cuantos = 4): number[] {
  const rango = max - min || 1;
  const bruto = rango / cuantos;
  const magnitud = Math.pow(10, Math.floor(Math.log10(bruto)));
  const paso = [1, 2, 2.5, 5, 10].map((m) => m * magnitud).find((p) => p >= bruto) ?? magnitud * 10;
  const salida: number[] = [];
  for (let v = Math.ceil(min / paso) * paso; v <= max + 1e-9; v += paso) salida.push(Number(v.toFixed(6)));
  return salida;
}

export function Ejes({ ancho, alto, tx, ty, ticksY, etiquetasX, unidad, referencia, formato, cuerpo = 10, maxEtiquetas = 8 }: {
  ancho: number; alto: number;
  tx: (v: number | string) => number;
  ty: (v: number) => number;
  ticksY: number[];
  etiquetasX: (string | number)[];
  unidad?: string;
  referencia?: number;
  formato?: (v: number) => string;
  /** Tamaño del texto del eje. En móvil sube, porque el viewBox se reduce y con él la escala. */
  cuerpo?: number;
  /** Cuántas etiquetas del eje X caben. Ocho no caben en un viewBox de 360. */
  maxEtiquetas?: number;
}) {
  const fmt = formato ?? ((v: number) => String(Number(v.toFixed(2))));
  const cadaCuantas = Math.ceil(etiquetasX.length / maxEtiquetas);
  return (
    <g aria-hidden="true">
      {/* Rejilla recesiva: guía la lectura y no compite con las marcas. */}
      {ticksY.map((v) => (
        <g key={`y${v}`}>
          <line x1={MARGEN.izquierda} x2={ancho - MARGEN.derecha} y1={ty(v)} y2={ty(v)} stroke="var(--borde)" strokeWidth="1" />
          <text x={MARGEN.izquierda - 8} y={ty(v)} textAnchor="end" dominantBaseline="middle" fontSize={cuerpo} fill="var(--texto-3)">
            {fmt(v)}
          </text>
        </g>
      ))}
      {referencia !== undefined && (
        <line x1={MARGEN.izquierda} x2={ancho - MARGEN.derecha} y1={ty(referencia)} y2={ty(referencia)}
              stroke="var(--borde-fuerte)" strokeWidth="1" strokeDasharray="4 3" />
      )}
      <line x1={MARGEN.izquierda} x2={ancho - MARGEN.derecha} y1={alto - MARGEN.abajo} y2={alto - MARGEN.abajo} stroke="var(--borde-fuerte)" strokeWidth="1" />
      {etiquetasX.map((e, i) =>
        i % cadaCuantas === 0 ? (
          <text key={`x${e}${i}`} x={tx(e)} y={alto - MARGEN.abajo + cuerpo + 4} textAnchor="middle" fontSize={cuerpo} fill="var(--texto-3)">
            {typeof e === "string" && e.length > 7 ? e.slice(-5) : e}
          </text>
        ) : null,
      )}
      {unidad && (
        <text x={MARGEN.izquierda - 8} y={MARGEN.arriba - 5} textAnchor="end" fontSize={cuerpo - 1} fill="var(--texto-3)">
          {unidad}
        </text>
      )}
    </g>
  );
}

/** Leyenda: SIEMPRE presente con dos o más series, para que la identidad no dependa del color.
 *  Con una sola serie no hay leyenda: el título ya la nombra. */
export function Leyenda({ items }: { items: { nombre: string; color: string }[] }) {
  if (items.length < 2) return null;
  return (
    <ul className="leyenda">
      {items.map((s) => (
        <li key={s.nombre}>
          <span className="leyenda-marca" style={{ background: s.color }} aria-hidden="true" />
          {s.nombre}
        </li>
      ))}
    </ul>
  );
}

export function Tooltip({ x, y, ancho, lineas }: { x: number; y: number; ancho: number; lineas: string[] }) {
  const w = Math.max(...lineas.map((l) => l.length)) * 6.1 + 18;
  const h = lineas.length * 14 + 12;
  const izq = Math.min(Math.max(x + 10, 4), Math.max(ancho - w - 4, 4));
  return (
    <g pointerEvents="none">
      <rect x={izq} y={Math.max(y - h - 8, 2)} width={w} height={h} rx="6" fill="var(--superficie-0)" stroke="var(--borde-fuerte)" strokeWidth="1" />
      {lineas.map((l, i) => (
        <text key={i} x={izq + 9} y={Math.max(y - h - 8, 2) + 16 + i * 14} fontSize="11" fill={i === 0 ? "var(--texto-1)" : "var(--texto-2)"}>
          {l}
        </text>
      ))}
    </g>
  );
}

export function Marco({ titulo, idioma, ancho, alto, children, describe }: {
  titulo: Bilingue; idioma: Idioma; ancho: number; alto: number; children: React.ReactNode; describe?: string;
}) {
  const id = useId();
  return (
    <figure className="grafico" role="group" aria-labelledby={id}>
      <figcaption id={id} className="grafico-titulo">{bi(titulo, idioma)}</figcaption>
      {/* `aspect-ratio` + `max-width`, no `height: alto`.
          · El alto fijo en píxeles estaba bien en ESCRITORIO, donde la restricción es el alto: el
            contenido cabía a escala 1,0. En MÓVIL la restricción es el ancho, así que el SVG se
            encajaba dentro dejando franjas vacías — medido en un iPhone, un lienzo de 332 px con
            160 px de contenido: el **52 % del alto** desperdiciado.
          · Con solo la proporción, en escritorio el gráfico se estiraba a escala 1,55 y las
            etiquetas de 11 px salían a 17 — más grandes que el cuerpo del texto, cuando el
            diseño las quiere recesivas.
          El tope de ancho resuelve las dos: nunca escala por encima de 1,0 y en pantalla estrecha
          se reduce con su proporción intacta. Sin condicionales ni medir el contenedor. */}
      <div className="grafico-lienzo" style={{ aspectRatio: `${ancho} / ${alto}`, maxWidth: ancho }}
           role="img" aria-label={describe || bi(titulo, idioma)}>
        {children}
      </div>
    </figure>
  );
}

/** Estado de hover compartido: qué marca está activa. */
export function useHover<T>() {
  return useState<T | null>(null);
}
